import { ChangeEventHandler, KeyboardEventHandler, useCallback, useRef, useState } from 'react'

import { focusInput } from './focusInput'
import { validateEmail, ValidateEmailReturn } from './validateEmail'

const defaultInputs = ['']

export const useInputs = () => {
  const inputsRef = useRef<HTMLDivElement>(null)
  const [inputs, setInputs] = useState<string[]>(defaultInputs)
  const [validationMap, setValidationMap] = useState<Record<string, ValidateEmailReturn>>({})

  const inputsCount = inputs.length

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    e => {
      e.persist()
      setInputs(prev =>
        prev.map((input, index) => {
          if (validationMap[index] && `${index}` === e.target.name) {
            setValidationMap(state => ({ ...state, [index]: undefined }))
          }

          return `${index}` === e.target.name ? e.target.value : input
        }),
      )
    },
    [validationMap],
  )

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    e => {
      const { name, value }: { name: string; value: string } = e.target as HTMLInputElement
      if (e.key === 'Backspace' && !value) {
        if (inputsCount > defaultInputs.length) {
          setInputs(prev => prev.filter((_input, index) => `${index}` !== name))
        }
        setTimeout(() => {
          if (!inputsRef.current) {
            return
          }
          focusInput(inputsRef.current, +name - 1)
        }, 0)
      }
      if (e.key === 'ArrowUp' && inputsRef.current) {
        focusInput(inputsRef.current, +name - 1)
      }
      if (e.key === 'ArrowDown' && inputsRef.current) {
        focusInput(inputsRef.current, +name + 1)
      }
    },
    [inputsCount],
  )

  const onAddMoreClick = useCallback(() => {
    setInputs(prev => [...prev, ''])
    setTimeout(() => {
      if (!inputsRef.current) {
        return
      }
      const box = inputsRef.current.firstElementChild
      if (!box) {
        return
      }
      box.scrollTop = box.scrollHeight
      focusInput(inputsRef.current, inputsCount)
    }, 0)
  }, [inputsCount])

  const onEnterPressed = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    e => {
      if (!inputsRef.current) {
        return
      }
      const { name } = e.target as HTMLInputElement
      const index = +name
      if (index === inputsCount - 1) {
        onAddMoreClick()
        return
      }
      focusInput(inputsRef.current, index + 1)
    },
    [inputsCount, onAddMoreClick],
  )

  return {
    inputs,
    inputsRef,
    onChange,
    onKeyDown,
    onAddMoreClick,
    onEnterPressed,
    validationMap,
    setValidationMap,
  }
}
